import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tags, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TimesheetCategories = () => {
  const { getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/timesheets/categories`, { headers: getAuthHeaders() });
      if (res.ok) setCategories(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/timesheets/categories`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.ok) {
        setNewName('');
        fetchCategories();
        toast.success('Category added');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add');
      }
    } catch { toast.error('Network error'); }
    finally { setAdding(false); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      const res = await fetch(`${API}/api/timesheets/categories/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() }),
      });
      if (res.ok) {
        setEditingId(null);
        fetchCategories();
        toast.success('Category updated');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Network error'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/timesheets/categories/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchCategories();
        toast.success('Category removed');
      }
    } catch { toast.error('Network error'); }
  };

  return (
    <div className="space-y-6" data-testid="timesheet-categories-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Timesheet Categories</h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Manage the task categories available for timesheets</p>
      </div>

      {/* Add new */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Category
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Category name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              data-testid="new-category-input"
            />
            <Button onClick={handleAdd} disabled={adding || !newName.trim()} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-category-btn">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Tags className="h-4 w-4" /> Categories ({categories.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
          ) : categories.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6">No categories yet</p>
          ) : (
            <div className="space-y-1" data-testid="category-list">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 group" data-testid={`category-${cat.id}`}>
                  {editingId === cat.id ? (
                    <>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdate(cat.id)}
                        className="h-8 text-sm"
                        autoFocus
                      />
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => handleUpdate(cat.id)}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-400" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Tags className="h-4 w-4 text-zinc-400" />
                      <span className="flex-1 text-sm text-zinc-800 dark:text-zinc-200">{cat.name}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-blue-600" onClick={() => { setEditingId(cat.id); setEditName(cat.name); }} data-testid={`edit-cat-${cat.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-600" onClick={() => handleDelete(cat.id, cat.name)} data-testid={`delete-cat-${cat.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimesheetCategories;
